<script setup>
/**
 * UserSidebarSection Component
 * Gamified user section for sidebar with level badge, points, and progress.
 * Clickable to navigate to profile page.
 */
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { doc } from 'firebase/firestore'
import { getFirebaseDb } from '../../../core/firebase/firebase.js'
import { useAuthStore } from '../../../app/store/index.js'
import { ROUTES } from '../../../core/config/constants.js'
import {
  subscribeLevelsConfig,
  computeLevelFromPoints,
  ensureUserXpFields,
} from '../../../core/services/levels.js'
import { useFirestoreSubscription } from '@core/composables/useFirestoreSubscription.js'

// ============================================
// Props
// ============================================
const props = defineProps({
  expanded: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['navigate'])

// ============================================
// State
// ============================================
const router = useRouter()
const authStore = useAuthStore()

// User profile data (realtime from Firestore)
const userProfile = ref(null)
const profileLoading = ref(true)

// Linked client doc from ClientsV2/{client_makat}
const clientDoc = ref(null)

// Levels config
const levelsConfig = ref(null)
const levelsLoading = ref(true)

const { subscribe, track, unsubscribeAll } = useFirestoreSubscription()

// Track profile/client unsubs separately for watch re-subscription
let unsubProfile = null
let unsubClient = null

// ============================================
// Computed
// ============================================
const userId = computed(() => authStore.userId)
const isReady = computed(() => !profileLoading.value && !levelsLoading.value)

// Role checks
const isClient = computed(() => authStore.userRole === 'client')
const { isAdmin, isAgent } = storeToRefs(authStore)

// Agent number
const agentNumber = computed(() => {
  return userProfile.value?.agent_number || null
})

// Avatar: from Firestore profile only (managed by our storage)
const photoURL = computed(() => {
  return userProfile.value?.photoURL || null
})

const initials = computed(() => {
  const name = displayName.value || ''
  if (!name) return '?'
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

// Name & client
const displayName = computed(() => {
  if (userProfile.value?.name) return userProfile.value.name
  if (authStore.user?.authData?.displayName) return authStore.user.authData.displayName
  if (authStore.user?.authData?.email) return authStore.user.authData.email
  return 'משתמש'
})

const clientName = computed(() => {
  return clientDoc.value?.name || userProfile.value?.client_name || null
})

// XP & Level
const userPoints = computed(() => {
  return userProfile.value?.xp_points || 0
})

const levelInfo = computed(() => {
  return computeLevelFromPoints(userPoints.value, levelsConfig.value)
})

const progressPercent = computed(() => {
  return Math.round(levelInfo.value.progressRatio * 100)
})

// ============================================
// Actions
// ============================================
function navigateToProfile() {
  if (!isReady.value) return
  emit('navigate')
  router.push({ name: ROUTES.PROFILE })
}

function handleKeydown(e) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    navigateToProfile()
  }
}

// ============================================
// Subscriptions
// ============================================
function subscribeToUserProfile(uid) {
  if (!uid) {
    profileLoading.value = false
    return
  }

  profileLoading.value = true
  const db = getFirebaseDb()
  const docRef = doc(db, 'Users', uid)

  unsubProfile = subscribe(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        userProfile.value = { id: snapshot.id, ...snapshot.data() }
      } else {
        userProfile.value = null
      }
      profileLoading.value = false
    },
    (error) => {
      console.error('User profile subscription error:', error)
      profileLoading.value = false
    }
  )
}

function subscribeToClient(clientMakat) {
  if (unsubClient) {
    unsubClient()
    unsubClient = null
  }

  if (!clientMakat) {
    clientDoc.value = null
    return
  }

  const db = getFirebaseDb()
  const docRef = doc(db, 'ClientsV2', clientMakat)

  unsubClient = subscribe(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        clientDoc.value = { id: snapshot.id, ...snapshot.data() }
      } else {
        clientDoc.value = null
      }
    },
    (error) => {
      console.error('Client subscription error:', error)
      clientDoc.value = null
    }
  )
}

// ============================================
// Lifecycle
// ============================================
onMounted(async () => {
  // Subscribe to levels config
  track(
    subscribeLevelsConfig((config, err) => {
      if (!err) {
        levelsConfig.value = config
      }
      levelsLoading.value = false
    })
  )

  // Subscribe to user profile
  if (userId.value) {
    subscribeToUserProfile(userId.value)

    // Ensure XP fields exist
    try {
      await ensureUserXpFields(userId.value)
    } catch (err) {
      console.error('Failed to ensure XP fields:', err)
    }
  }
})

// Watch for user changes
watch(
  () => userId.value,
  (newUid, oldUid) => {
    if (newUid !== oldUid) {
      if (unsubProfile) {
        unsubProfile()
        unsubProfile = null
      }
      if (unsubClient) {
        unsubClient()
        unsubClient = null
      }
      userProfile.value = null
      clientDoc.value = null
      if (newUid) {
        subscribeToUserProfile(newUid)
      }
    }
  }
)

// Re-subscribe to the linked client whenever the session's active client
// changes. We watch the IN-MEMORY auth value rather than the Firestore
// Users doc so sub-client switches (which never touch Firestore) still
// update the displayed client name.
watch(
  () => authStore.user?.profile?.client_makat,
  (newMakat) => {
    subscribeToClient(newMakat)
  },
  { immediate: true }
)
</script>

<template>
  <button
    class="user-section-btn"
    :class="{ expanded: props.expanded, collapsed: !props.expanded, loading: !isReady }"
    :disabled="!isReady"
    :aria-label="isReady ? 'פתח פרופיל' : 'טוען...'"
    @click="navigateToProfile"
    @keydown="handleKeydown"
  >
    <!-- Expanded View -->
    <template v-if="props.expanded">
      <div class="user-section-expanded">
        <!-- Avatar -->
        <div class="user-avatar-wrapper">
          <img v-if="photoURL" :src="photoURL" :alt="displayName" class="user-avatar-img" />
          <div v-else class="user-avatar-placeholder">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="sidebarAvatarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#283548" />
                  <stop offset="50%" stop-color="#334155" />
                  <stop offset="100%" stop-color="#64748b" />
                </linearGradient>
              </defs>
              <circle cx="24" cy="24" r="24" fill="url(#sidebarAvatarGrad)" />
              <circle cx="24" cy="19" r="7" fill="rgba(255,255,255,0.9)" />
              <path
                d="M24 29c-7 0-12 4-12 9v1c0 0.5 0.3 1 1 1.5 2.5 1.5 6.5 2.5 11 2.5s8.5-1 11-2.5c0.7-0.5 1-1 1-1.5v-1c0-5-5-9-12-9z"
                fill="rgba(255,255,255,0.9)"
              />
            </svg>
          </div>

          <!-- Level badge overlay (clients only) -->
          <div v-if="isClient" class="level-badge-mini" :class="`level-${levelInfo.level}`">
            {{ levelInfo.level }}
          </div>
        </div>

        <!-- Info -->
        <div class="user-info-section">
          <div class="user-name-row">
            <span class="user-display-name">{{ displayName }}</span>
          </div>
          <div class="user-client-row">
            <span class="user-client">{{ clientName || 'ללא שיוך לקוח' }}</span>
          </div>

          <!-- Level & Points Row (clients only) -->
          <div v-if="isClient" class="level-points-row">
            <div class="level-tag" :class="`level-${levelInfo.level}`">
              <svg
                class="level-icon"
                viewBox="0 0 24 24"
                width="12"
                height="12"
                fill="currentColor"
              >
                <polygon
                  points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                />
              </svg>
              <span>Lv {{ levelInfo.level }}</span>
            </div>
            <div class="points-tag">
              <span class="points-value">{{ userPoints }}</span>
              <span class="points-label">נק׳</span>
            </div>
          </div>

          <!-- Role badge (agents & admins) -->
          <div v-else class="role-badge-row">
            <div v-if="isAgent" class="role-badge agent">
              <span v-if="agentNumber">סוכן {{ agentNumber }}</span>
              <span v-else>סוכן</span>
            </div>
            <div v-else-if="isAdmin" class="role-badge admin">
              <span>משתמש מנהל</span>
            </div>
          </div>

          <!-- Progress Bar (clients only) -->
          <div v-if="isClient" class="progress-section">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
            <div class="progress-text">
              <template v-if="levelInfo.isMaxLevel">
                <span class="max-level-text">רמה מקסימלית</span>
              </template>
              <template v-else> עוד {{ levelInfo.pointsToNext }} לרמה הבאה </template>
            </div>
          </div>
        </div>

        <!-- Arrow indicator -->
        <div class="arrow-indicator">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </div>
      </div>
    </template>

    <!-- Collapsed View -->
    <template v-else>
      <div class="user-section-collapsed">
        <div class="user-avatar-wrapper collapsed">
          <img v-if="photoURL" :src="photoURL" :alt="displayName" class="user-avatar-img" />
          <div v-else class="user-avatar-placeholder">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="sidebarAvatarGradCollapsed" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#283548" />
                  <stop offset="50%" stop-color="#334155" />
                  <stop offset="100%" stop-color="#64748b" />
                </linearGradient>
              </defs>
              <circle cx="24" cy="24" r="24" fill="url(#sidebarAvatarGradCollapsed)" />
              <circle cx="24" cy="19" r="7" fill="rgba(255,255,255,0.9)" />
              <path
                d="M24 29c-7 0-12 4-12 9v1c0 0.5 0.3 1 1 1.5 2.5 1.5 6.5 2.5 11 2.5s8.5-1 11-2.5c0.7-0.5 1-1 1-1.5v-1c0-5-5-9-12-9z"
                fill="rgba(255,255,255,0.9)"
              />
            </svg>
          </div>

          <!-- Level badge overlay (clients only) -->
          <div
            v-if="isClient"
            class="level-badge-mini collapsed"
            :class="`level-${levelInfo.level}`"
          >
            {{ levelInfo.level }}
          </div>
        </div>
      </div>
    </template>

    <!-- Loading skeleton -->
    <div v-if="!isReady" class="loading-overlay">
      <div class="skeleton-pulse"></div>
    </div>
  </button>
</template>

<style scoped>
/* ============================================
   Button Reset & Base
   ============================================ */
.user-section-btn {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: right;
  font-family: var(--font-family);
  position: relative;
  transition: all var(--transition-fast);
}

.user-section-btn:disabled {
  cursor: default;
}

.user-section-btn:focus {
  outline: none;
}

.user-section-btn:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* ============================================
   Expanded View
   ============================================ */
.user-section-expanded {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  transition: background var(--transition-fast);
}

.user-section-btn.expanded:hover:not(:disabled) .user-section-expanded {
  background: var(--bg-tertiary);
}

.user-section-btn.expanded:active:not(:disabled) .user-section-expanded {
  transform: scale(0.99);
}

/* Avatar */
.user-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.user-avatar-img {
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-full);
  object-fit: cover;
}

.user-avatar-placeholder {
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-full);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(40, 53, 72, 0.3);
}

.user-avatar-placeholder svg {
  width: 100%;
  height: 100%;
  display: block;
}

/* Level badge on avatar */
.level-badge-mini {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius-full);
  background: linear-gradient(135deg, #283548 0%, #334155 100%);
  color: white;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--bg-secondary);
  box-shadow: var(--shadow-sm);
}

.level-badge-mini.level-1 {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
}

.level-badge-mini.level-2 {
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
}

.level-badge-mini.level-3 {
  background: linear-gradient(135deg, #334155 0%, #64748b 100%);
}

.level-badge-mini.level-4 {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
}

/* User info section */
.user-info-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name-row {
  display: flex;
  align-items: center;
}

.user-display-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-client-row {
  display: flex;
  align-items: center;
}

.user-client {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Level & Points row */
.level-points-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-xs);
}

/* Role badge row */
.role-badge-row {
  display: flex;
  align-items: center;
  margin-top: var(--spacing-xs);
}

.role-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
}

.role-badge.agent {
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
}

.role-badge.admin {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.level-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
  background: rgba(40, 53, 72, 0.15);
  color: var(--color-primary);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
}

.level-tag.level-1 {
  background: rgba(107, 114, 128, 0.15);
  color: #6b7280;
}

.level-tag.level-2 {
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
}

.level-tag.level-3 {
  background: rgba(51, 65, 85, 0.15);
  color: #334155;
}

.level-tag.level-4 {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.level-icon {
  opacity: 0.8;
}

.points-tag {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  font-size: 11px;
}

.points-value {
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.points-label {
  color: var(--text-muted);
}

/* Progress section */
.progress-section {
  margin-top: var(--spacing-xs);
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: var(--bg-tertiary);
  border-radius: var(--border-radius-full);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #283548 0%, #334155 100%);
  border-radius: var(--border-radius-full);
  transition: width 0.5s ease-out;
}

.progress-text {
  font-size: 9px;
  color: var(--text-muted);
  margin-top: 2px;
}

.max-level-text {
  color: #f59e0b;
}

/* Arrow indicator */
.arrow-indicator {
  display: flex;
  align-items: center;
  color: var(--text-muted);
  opacity: 0;
  transition:
    opacity var(--transition-fast),
    transform var(--transition-fast);
}

.user-section-btn.expanded:hover:not(:disabled) .arrow-indicator {
  opacity: 1;
}

.user-section-btn.expanded:active:not(:disabled) .arrow-indicator {
  transform: translateX(-2px);
}

/* ============================================
   Collapsed View
   ============================================ */
.user-section-collapsed {
  display: flex;
  justify-content: center;
  padding: var(--spacing-sm);
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  transition: background var(--transition-fast);
}

.user-section-btn.collapsed:hover:not(:disabled) .user-section-collapsed {
  background: var(--bg-tertiary);
}

.user-avatar-wrapper.collapsed .user-avatar-img,
.user-avatar-wrapper.collapsed .user-avatar-placeholder {
  width: 40px;
  height: 40px;
}

.level-badge-mini.collapsed {
  width: 16px;
  height: 16px;
  font-size: 9px;
  bottom: -1px;
  right: -1px;
}

/* ============================================
   Loading State
   ============================================ */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.skeleton-pulse {
  width: 60%;
  height: 8px;
  background: linear-gradient(
    90deg,
    var(--bg-tertiary) 25%,
    var(--border-color) 50%,
    var(--bg-tertiary) 75%
  );
  background-size: 200% 100%;
  border-radius: var(--border-radius);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.user-section-btn.loading {
  min-height: 80px;
}

.user-section-btn.collapsed.loading {
  min-height: 56px;
}
</style>
